/*    
<summary>
  This functional component "AddUpdateCustomerSetting" defines the props like initial values and functions for the child form component.
  Developer:Mohammad Saquib Khan, Created Date:04-June-2024
</summary>
<returns>Returns JSX</returns>
*/

import { observer } from "mobx-react";
import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { initialPageLimit } from "../../../../constants/pageLimit.constant";
import { useStore } from "../../../../contexts/store-provider";
import IPageInfo from "../../../../models/ICommon";
import { ISorting } from "../../../../models/ISorting";
import DeletePopup from "../../../../shared-components/popup/delete/delete-popup";
import DeletePopupHOC from "../../../../shared-components/popup/delete/delete-popup-hoc";
import FormLoader from "../../../../shared-components/FormLoader/FormLoader";
import { formatMessage } from "../../../../translations/format-message";
import PaymentListView from "./components/payment-list-view";
import AddPaymentSettingsForm from "./forms/add-payment-settings-form";

interface IProps {
  id: number;
  modalClosed: () => void;
  showDeletePopup: boolean;
  deletePopupToggleHandler: () => void;
}

const PaymentSettings = (props: IProps) => {
  const [isDetailsVisible, setIsDetailsVisible] = useState<boolean>(false);
  const { masterManagementStore } = useStore();
  const {
    addUpdatePaymentSettingState,
    GetAllPaymentService,
    getAllPaymentList,
    paymentSettingState,
    AddPaymentService,
    UpdatePaymentService,
    DeletePaymentService,
    GetPaymentByIdService,
    deletePaymentSettingState,
    getByIdPaymentSettingState,
    getPaymentDetail,
    resetGetAllPaymentSettingState,
    resetGetByIdPaymentSettingState,
    resetdDeletePaymentSettingState,
    resetAddUpdatePaymentSettingState,
  } = masterManagementStore;
  // const initialValues: IAddCustomerRate = addCustomerRateState;
  const initialValues = getPaymentDetail;

  const { id, modalClosed, showDeletePopup, deletePopupToggleHandler } = props;

  const onModalClosed = useCallback(() => {
    modalClosed();
    resetAddUpdatePaymentSettingState();
    resetdDeletePaymentSettingState();
    resetGetAllPaymentSettingState();
    resetGetByIdPaymentSettingState();
  }, [modalClosed]);

  const setIsRefreshPage = () => {
    setPageInfo({ ...pageInfo, isRefreshPage: false });
  };

  const handleVisibility = () => {
    setIsDetailsVisible((e) => !e);
  };
  const [selectedId, setSelectedId] = useState<number>(-1);
  const [editableId, setEditableId] = useState<number>(-1);
  const [disablePagination, setDisablePagination] = useState<boolean>(false);
  const [pageLimit, setPageLimit] = useState<number>(initialPageLimit);
  const [pageInfo, setPageInfo] = useState<IPageInfo>({
    currentPage: 1,
    totalPages: 1,
    isPagerChange: false,
    isRefreshPage: false,
  });
  const onPageChanged = useCallback((pageNumber: number) => {
    setPageInfo((pageInfo) => ({ ...pageInfo, currentPage: pageNumber }));
  }, []);
  const onPageLimitChanged = (evt: React.ChangeEvent<HTMLSelectElement>) => {
    let value = parseInt(evt.target.value);
    setPageLimit(value);
  };
  const [sorting, setSorting] = useState<ISorting>({
    column: "Name",
    orderBy: "asc",
  });
  const [isApiCalled, setIsApiCalled] = useState<boolean>(false);

  const callGetPaymentList = useCallback(() => {
    setDisablePagination(true);
    GetAllPaymentService();
    setIsApiCalled(true);
  }, [GetAllPaymentService]);

  // useEffect(() => {
  //   setPageInfo((pageInfo) => ({
  //     ...pageInfo,
  //     // totalPages: customerRateList?.TotalPages,
  //     totalPages: 1,
  //   }));
  //   setDisablePagination(false);
  // }, [getAllPaymentList]);
  // }, [getAllPaymentList, customerRateList.TotalPages]);

  const handleSortingChange = (accessor: string) => {
    let sortOrder = sorting.orderBy;
    if (accessor === sorting.column) {
      sortOrder = sorting.orderBy === "asc" ? "desc" : "asc";
    }
    setSorting({ column: accessor, orderBy: sortOrder });
  };

  const onSubmitForm = (values: any) => {
    let finalValues = {
      ...values,
    };
    if (!(values.Id && values.Id > 0)) {
      AddPaymentService(finalValues);
    } else {
      UpdatePaymentService(values.Id, finalValues);
    }
  };

  const deleteHandler = () => {
    DeletePaymentService(selectedId);
    deletePopupToggleHandler();
    setEditableId(-1);
    resetAddUpdatePaymentSettingState();
  };

  const editHandler = (row: { Id: number }) => {
    setEditableId(row.Id);
    GetPaymentByIdService(row.Id);
  };

  useEffect(() => {
    callGetPaymentList();
  }, [callGetPaymentList]);

  useEffect(() => {
    if (addUpdatePaymentSettingState.success) {
      if (editableId > 0) {
        toast.success(formatMessage("payment_setting_update_success"));
      } else {
        toast.success(formatMessage("payment_setting_add_success"));
      }
      callGetPaymentList();
      resetAddUpdatePaymentSettingState();
      setEditableId(-1);
    }
  }, [
    addUpdatePaymentSettingState.success,
    callGetPaymentList,
    resetAddUpdatePaymentSettingState,
    editableId,
  ]);

  useEffect(() => {
    if (deletePaymentSettingState.success) {
      resetdDeletePaymentSettingState();
      toast.success(formatMessage("payment_setting_delete_success"));
      callGetPaymentList();
    }
  }, [
    deletePaymentSettingState.success,
    callGetPaymentList,
    resetdDeletePaymentSettingState,
  ]);

  return (
    <React.Fragment>
      {(getByIdPaymentSettingState.inProgress ||
        addUpdatePaymentSettingState.inProgress ||
        deletePaymentSettingState.inProgress ||
        paymentSettingState.inProgress) && (
        <FormLoader
          loading={
            getByIdPaymentSettingState.inProgress ||
            addUpdatePaymentSettingState.inProgress ||
            deletePaymentSettingState.inProgress ||
            paymentSettingState.inProgress
          }
        />
      )}

      {showDeletePopup && (
        <DeletePopup
          title="delete_payment"
          modalSubmit={deleteHandler}
          modalClosed={deletePopupToggleHandler}
          isLoading={deletePaymentSettingState.inProgress}
          message="delete_payment_confirm"
        />
      )}

      <AddPaymentSettingsForm
        submitHandler={onSubmitForm}
        addUpdateInProgress={addUpdatePaymentSettingState.inProgress}
        actionSuccess={addUpdatePaymentSettingState.success}
        initialValues={initialValues}
        id={editableId}
        onCancelEdit={() => {
          setEditableId(-1);
          resetAddUpdatePaymentSettingState();
        }}
      />
      <PaymentListView
        setVisiblity={handleVisibility}
        id={id}
        showDeletePopup={showDeletePopup}
        deleteToggleHandler={deletePopupToggleHandler}
        onPageLimitChanged={onPageLimitChanged}
        pageLimit={pageLimit}
        pageInfo={pageInfo}
        onPageChanged={onPageChanged}
        disablePagination={disablePagination}
        sorting={sorting}
        handleSortingChange={handleSortingChange}
        setSelectedId={setSelectedId}
        selectedId={selectedId}
        editHandler={editHandler}
        setIsRefreshPage={setIsRefreshPage}
      />
    </React.Fragment>
  );
};

export default DeletePopupHOC(observer(PaymentSettings));
